import {
  subMonths,
  format,
  isToday,
  isTomorrow,
  addHours,
  isSameDay,
} from 'date-fns'

export const getOneMonthAgoIso = (): string =>
  subMonths(new Date(), 1).toISOString()

/**
 * Current year-month, e.g. "2026-07". Used to scope the monthly comment
 * cleanup banner's dismissal: a new key each month lets the banner reappear
 * at the start of every month while a dismissal only silences it for the
 * month it was dismissed in.
 */
export const getCurrentMonthKey = (): string => format(new Date(), 'yyyy-MM')

/**
 * Formats a date as a human-readable sync time range string.
 * Returns "to run soon" if the date is in the past or invalid.
 * Otherwise returns a 2-hour time range:
 * - "between 2:30 PM and 4:30 PM today" if the date is today
 * - "between 11:30 PM today and 1:30 AM tomorrow" if crossing midnight
 * - "between 9:15 AM and 11:15 AM tomorrow" if the date is tomorrow
 * - "between 3:45 PM and 5:45 PM on January 15" for other dates
 *
 * @param nextSyncDate - The date to format
 * @param currentDate - The current date to compare against (defaults to now)
 * @returns Formatted sync time range string
 */
export const getFormattedNextSyncTimeRange = (
  nextSyncDate: Date,
  currentDate: Date = new Date(),
): string => {
  // Guard against invalid Date inputs
  if (Number.isNaN(nextSyncDate.getTime())) {
    return 'to run soon'
  }

  const endDate = addHours(nextSyncDate, 2)

  // If endDate is in the past, show "to run soon"
  if (endDate.getTime() <= currentDate.getTime()) {
    return 'to run soon'
  }
  const startTime = format(nextSyncDate, 'h:mm a')
  const endTime = format(endDate, 'h:mm a')

  // Check if the time range crosses into a different day
  const spansMultipleDays = !isSameDay(nextSyncDate, endDate)

  if (isToday(nextSyncDate)) {
    if (spansMultipleDays) {
      // Crosses midnight from today to tomorrow
      return `between ${startTime} today and ${endTime} tomorrow`
    }
    return `between ${startTime} and ${endTime} today`
  } else if (isTomorrow(nextSyncDate)) {
    if (spansMultipleDays) {
      // Crosses midnight from tomorrow to the day after
      const dayAfterTomorrow = format(endDate, 'EEEE')
      return `between ${startTime} tomorrow and ${endTime} ${dayAfterTomorrow}`
    }
    return `between ${startTime} and ${endTime} tomorrow`
  } else {
    const startDay = format(nextSyncDate, 'MMMM d')
    if (spansMultipleDays) {
      // Crosses midnight on a future date
      const endDay = format(endDate, 'MMMM d')
      return `between ${startTime} on ${startDay} and ${endTime} on ${endDay}`
    }
    return `between ${startTime} and ${endTime} on ${startDay}`
  }
}
